// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Physical constants of a real pinball machine.
//
// These are measurements, not tuning knobs. Every value here is a fact about a
// Bally/Williams standard body and its 1-1/16 inch carbon-steel ball. Tuning happens in
// the material table and the coil strengths, never here.
//
// See 02-physics-and-feel-specification.md §2 and §5.

// ---------------------------------------------------------------------------
// The playfield
// ---------------------------------------------------------------------------

/** Playable width, 20.25 in. Metres. */
export const PLAYFIELD_WIDTH = 0.51435
/** Playable length, 42 in. Metres. */
export const PLAYFIELD_LENGTH = 1.0668

export const PLAYFIELD_HALF_WIDTH = PLAYFIELD_WIDTH / 2 // 0.257175
export const PLAYFIELD_HALF_LENGTH = PLAYFIELD_LENGTH / 2 // 0.5334

/**
 * Underside of the playfield glass, above the playfield surface. 4 in.
 *
 * **This was 0.0206 m and that was a bug** — 13/16 of an inch, less than the ball's own
 * 0.0269875 m diameter, so the ball did not fit in the machine. Every ball was in permanent
 * contact with the glass and was being pushed 6.4 mm into the playfield on every step.
 * Nothing in the planar game showed it, because both the floor and the glass push along
 * ±Z only, which is exactly why it survived a full suite of passing tests: the ball sat
 * below the surface and every planar measurement was unaffected. The moment anything
 * height-aware arrived — a ramp, a habitrail, a trigger's `zRange` — it surfaced at once.
 *
 * The clearance is set by what has to fit underneath, not by the ball. A ramp apex around
 * 60 mm plus a 27 mm ball already needs 87 mm, so the constraint is the machine's tallest
 * layer and not its ball. Four inches clears a realistic ramp and habitrail while leaving
 * airballs able to reach the glass: a ball's crown touches it from 1.21 m/s of vertical
 * velocity, which a slingshot or a ramp edge produces routinely.
 *
 * A **calibration seed, not a measurement** — override per machine via `WorldOptions`, which
 * is what the definition's `physics` block will drive. **[VERIFY on a real machine]**
 */
export const GLASS_HEIGHT = 0.1016

/** Standard incline, degrees and radians. */
export const INCLINE_DEGREES = 6.5
export const INCLINE_RADIANS = 0.11344640137963143

// ---------------------------------------------------------------------------
// Gravity
// ---------------------------------------------------------------------------

/** Standard gravity, m/s². */
export const G = 9.80665

/**
 * Acceleration down the playfield: `g · sin(6.5°)`.
 *
 * **The single most important number in pinball.** About one ninth of gravity, and the
 * reason a pinball feels heavy and deliberate rather than bouncy. Get this wrong and
 * nothing else can rescue the feel.
 */
export const A_DOWNFIELD = 1.1101442962970425

/** Acceleration into the playfield: `g · cos(6.5°)`. Drives the friction normal force. */
export const A_NORMAL = 9.7436114384708059

/**
 * What a *rolling* ball actually accelerates at: `(5/7) · g · sin(6.5°)` ≈ 0.793 m/s².
 *
 * Not a simulation input — a consequence, recorded here because it is a genuine and
 * easily-missed fact. Once a ball stops sliding and starts rolling, two sevenths of the
 * gravitational work goes into spinning it up rather than moving it down the playfield.
 * A ball released at the top therefore reaches the flipper line noticeably later than
 * `A_DOWNFIELD` alone predicts, and any test asserting the naive figure is wrong rather
 * than the simulation.
 */
export const A_DOWNFIELD_ROLLING = 0.79296021164074459

// ---------------------------------------------------------------------------
// The ball
// ---------------------------------------------------------------------------

/** Diameter, 1-1/16 in. Metres. */
export const BALL_DIAMETER = 0.0269875
export const BALL_RADIUS = 0.01349375

/** Mass, 2-7/8 oz. Kilograms. */
export const BALL_MASS = 0.0806
export const BALL_INV_MASS = 1 / BALL_MASS

/** Moment of inertia of a solid sphere, `(2/5)·m·r²`. */
export const BALL_INERTIA = 0.4 * BALL_MASS * BALL_RADIUS * BALL_RADIUS
export const BALL_INV_INERTIA = 1 / BALL_INERTIA

/**
 * Tangential effective-mass denominator at a contact on the ball's surface:
 * `1/m + r²/I`. For a solid sphere this is `7/(2m)`, the classic rolling result.
 */
export const BALL_TANGENT_INV_MASS = BALL_INV_MASS + (BALL_RADIUS * BALL_RADIUS) / BALL_INERTIA

// ---------------------------------------------------------------------------
// Ramps, as a ball on the playfield below them meets them
// ---------------------------------------------------------------------------

/** A ramp floor's thickness, metres — the renderer's `RAMP_THICKNESS`. A ramp whose floor is
 *  lower than `2·BALL_RADIUS + RAMP_FLOOR_THICKNESS` is one a playfield ball cannot pass under. */
export const RAMP_FLOOR_THICKNESS = 0.003

/** The ball has to fit in the machine. Asserted by a test, because it once did not. */
export const GLASS_CLEARANCE_OK = GLASS_HEIGHT > BALL_DIAMETER

// ---------------------------------------------------------------------------
// Integration
// ---------------------------------------------------------------------------

/** Fixed simulation rate. Never varies, on any device, under any load. */
export const STEP_HZ = 1000
export const STEP_DT = 1 / STEP_HZ

/**
 * How far a flipper's contact normal tilts up out of the playfield plane, radians (G7-01).
 *
 * **Zero, which is the shipped physics exactly**, and the whole reason this constant exists is to
 * be something other than zero *in a measurement run* rather than in the product.
 *
 * PH-A15's note names the question this answers: the bat is a capsule **in the playfield plane**,
 * so its contact normal lies in that plane, so the friction impulse lies in it too — and a flipper
 * therefore imparts sidespin and can never impart backspin. 02 §9 records that as *"a change to the
 * model rather than a coefficient"*, and it is the one remaining hypothesis for PH-A03: a live
 * catch physically *is* rubber converting a ball's downward momentum into spin, and this model has
 * no direction for that momentum to go.
 *
 * A real flipper's rubber sits a little below the ball's equator, so the true normal tilts up. The
 * tilt is what would give the friction impulse a component along the playfield normal — backspin
 * about a horizontal axis — and it is the smallest change to the model that could produce one.
 *
 * **This is a measurement, not a tuning knob.** 02 §9 has already ruled out inertia, restitution,
 * contact point and approach angle, each with numbers; the value that would ship, if any, is a
 * decision for the calibration pass against a real machine (V5 … V11) and not for this file. The
 * harness varies it with `PINBALL_FLIPPER_TILT`; every fixture, every parity run and every game is
 * measured at zero and is bit-identical to what it was before this existed.
 */
export const FLIPPER_CONTACT_TILT = 0

/**
 * The largest tilt the rig will accept, radians. About 20°.
 *
 * A bound rather than a validation: a tilt beyond this is not a flipper whose rubber sits slightly
 * low, it is a flipper pointing at the glass, and a harness run that produced one would be
 * measuring a machine nobody could build.
 */
export const MAX_FLIPPER_CONTACT_TILT = 0.35

/**
 * Largest number of balls in play at once (PH-032).
 *
 * Devices size their per-ball state arrays to this, so a device can be constructed before
 * the world it will live in and still keep per-ball memory without allocating in the loop.
 */
export const MAX_BALLS = 6

/**
 * Hard speed clamp.
 *
 * A real ball peaks near 6 m/s off a flipper. 8 m/s leaves headroom for a solenoid stack
 * while keeping travel per step at 8 mm against a 13.49 mm radius — 0.59 radii, which the
 * swept narrow phase handles with margin.
 */
export const SPEED_CLAMP = 8.0

/** Above this many steps in one frame the simulation declares lost time instead of spiralling. */
export const MAX_STEPS_PER_FRAME = 40

/**
 * Below this separation speed a contact resolves to rest rather than to a tiny bounce.
 * Without it a cradled ball jitters forever, and PH-A01 fails.
 */
export const MIN_SEPARATION_VELOCITY = 0.02

/** Below this speed a ball is considered at rest for sleep and stuck-ball purposes. */
export const REST_SPEED = 0.005

/**
 * Normal impact speed below which a contact is not reported to `World.contactTap`.
 *
 * Presentation-only, and it changes no result: the solver resolves every contact regardless.
 * It exists because a ball rolling along a guide rail touches it on most of a thousand steps a
 * second, each touch a genuine non-resting contact of a centimetre a second — inaudible, and
 * enough of them to overrun a bounded feed and push out the impacts that *were* audible. The
 * continuous part of that contact is the rolling layer's job (07 §3.3); the tap reports
 * transients.
 *
 * Four times `MIN_SEPARATION_VELOCITY`. A ball landing from 0.3 mm — the scale of the solver's
 * own settling — arrives below it; one dropped from 3 mm arrives above it.
 */
export const CONTACT_REPORT_SPEED = 0.08

/** Positional correction is applied at this fraction per step, to avoid over-correction jitter. */
export const PENETRATION_CORRECTION = 1.0

/** Penetration below this is ignored — it is inside the noise of the contact solver. */
export const PENETRATION_SLOP = 1e-6

// ---------------------------------------------------------------------------
// The flipper (see 02 §8)
// ---------------------------------------------------------------------------

/**
 * The bat, to Bally/Williams spec.
 *
 * Measured against a real WPC playfield rather than assumed. The Williams bat is a Ø15 mm
 * base tapering to a Ø5 mm tip over 71 mm; the figures here are pivot-to-tip-centre with the
 * rubber on, which is what the ball actually touches.
 *
 * These were 0.0762 / 0.011 / 0.0075 — a bat 20 % longer and, at the tip, three times as
 * thick as the real part. It mattered for a reason that is not obvious: a 43.7 mm centre
 * drain out of a 76.2 mm bat *forces* the pivots 200 mm apart, against a real 6 13/16"–7",
 * and the pivots being 13.5 mm too far outboard is what put the fat end of the bat in the
 * return lane where it knocked balls back down the drain. The bat sets the pivots, and the
 * pivots are what plays wrong.
 */
/** Bat length, pivot to tip centre. */
export const FLIPPER_LENGTH = 0.0635
/** Bat radius at the pivot: the Ø15 mm base with its rubber. */
export const FLIPPER_RADIUS_PIVOT = 0.0105
/** Bat radius at the tip: the Ø5 mm tip with its rubber. */
export const FLIPPER_RADIUS_TIP = 0.0055
/** Rest-to-end-of-stroke sweep, 52°. Measured on real machines as the typical swing arc. */
export const FLIPPER_SWEEP = 0.9075712110370513

/**
 * Main coil strength, in the Visual Pinball unit `VPX_STRENGTH_TO_TORQUE` converts.
 *
 * The number PH-A07's 5.8–6.4 m/s band is calibrated against, and the single most
 * feel-sensitive value in the flipper. It lived as a literal in `Flipper`'s constructor while
 * every other default sat here, so the one figure a person tuning the flippers would come
 * looking for was the one figure not in the file they would look in.
 */
export const FLIPPER_STRENGTH = 3250

/**
 * Solenoid field build-up time.
 *
 * This is what makes a light tap possible: a very short press moves the bat a little way
 * at low torque. Remove it and tap passes become impossible and the flipper feels binary.
 */
export const FLIPPER_COIL_RAMP_SECONDS = 0.025

/** End-of-stroke angle at which the coil switches to its hold winding. */
export const FLIPPER_EOS_ANGLE = 0.08726646259971647 // 5°
/** Hold torque as a fraction of the main coil. */
export const FLIPPER_EOS_TORQUE_RATIO = 0.29
/**
 * Return spring torque, N·m.
 *
 * **Absolute, not a fraction of the coil, and that changed on 19 Aug 2026.** It was
 * `FLIPPER_RETURN_RATIO = 0.1` — "the return spring is deliberately about a tenth of the coil" —
 * and the sentence read well right up until the coil was re-calibrated. Two faults, one cause:
 *
 * - **The number stopped meaning anything.** A correctly-sized coil is 7.664 N·m, so the same
 *   return spring is now 0.7 % of it. Nobody can look at 0.0072 and tell whether it is right.
 * - **Worse, retuning the coil silently retuned the spring.** `retune()` recomputed
 *   `returnTorque = coilTorque × ratio`, so the three harness tunings — swept to separate the
 *   ball at the same speed, expressly so that *only* the assembly differed — were each also
 *   quietly given a different return spring, the strongest 2.4× the signed-off one. An A/B
 *   whose control moves is not an A/B.
 *
 * A spring is sized against the bat it returns, not against the coil that opposes it. The value
 * is the one the ratio form used to produce at the old coil, so the return stroke is unchanged
 * at 52 ms (PH-A08b, 50–200 ms).
 */
export const FLIPPER_RETURN_TORQUE = 0.05535

/** Restitution of the mechanical stops, so the bat visibly rebounds. */
export const FLIPPER_STOP_RESTITUTION = 0.25

